/**
 * FLOW[47468,50377]: Client-side circuit breaker for AI contours (engagement-ai, ai-assistant).
 *
 * Раньше недоступность AI-эндпоинтов (например, старый бэкенд без engagement-ai/ai-assistant
 * → 404 на каждый запрос) вырождалась в ШТОРМ: авто-поллеры и авто-превью повторяли запрос,
 * глобальный onError сыпал тостами «каждую секунду по множеству окон», а лог заполнялся
 * повторными сетевыми ошибками.
 *
 * Этот модуль — единый источник правды об устойчивости AI-контуров на клиенте:
 *   - 404 / 405 / 501  → контур «недоступен на этом сервере» (unavailable) на всю сессию:
 *                        не ретраим, гасим сетевые вызовы на уровне интерсептора, БЕЗ тостов —
 *                        только тихий inline-бейдж «AI-подсказки недоступны».
 *   - 5xx / сеть / таймаут → «временная деградация» (degraded): экспоненциальный бэкофф с
 *                        потолком, максимум ОДИН предупреждающий тост за сессию на контур.
 *   - успех            → сброс в ok.
 *
 * Состояние иммутабельно (каждая смена — новый объект), чтобы React через useSyncExternalStore
 * корректно перерисовывал бейджи без лишних циклов.
 */
import type { AxiosError } from 'axios';
import { messageService } from '@/services/messageService';

export type AiContourFamily = 'engagement-ai' | 'ai-assistant';
export type AiContourStatus = 'ok' | 'degraded' | 'unavailable';

export interface AiContourState {
  status: AiContourStatus;
  /** epoch ms; окно бэкоффа при status === 'degraded' */
  degradedUntil: number;
  failureStreak: number;
  degradedToastShown: boolean;
}

export interface AiContourAvailability {
  /** можно ли сейчас обращаться к контуру */
  available: boolean;
  status: AiContourStatus;
  /** постоянная (на сессию) недоступность: 404/405/501 */
  unavailable: boolean;
  /** активное окно бэкоффа (5xx/сеть) прямо сейчас блокирует */
  degraded: boolean;
  reason: string | null;
}

/** Синтетическая ошибка «контур закрыт» — ею интерсептор гасит запрос, не выходя в сеть. */
export interface AiShortCircuitError extends Error {
  __aiCircuitShortCircuit: true;
  code: 'AI_CIRCUIT_OPEN';
  family: AiContourFamily;
  config?: unknown;
}

const DEGRADED_BASE_MS = 20_000;
const DEGRADED_MAX_MS = 5 * 60_000;

const UNAVAILABLE_REASON = 'AI-подсказки недоступны на этом сервере';
const DEGRADED_REASON = 'AI-подсказки временно недоступны';

function createInitialState(): AiContourState {
  return { status: 'ok', degradedUntil: 0, failureStreak: 0, degradedToastShown: false };
}

const families: Record<AiContourFamily, AiContourState> = {
  'engagement-ai': createInitialState(),
  'ai-assistant': createInitialState(),
};

const listeners = new Set<() => void>();

function emit(): void {
  listeners.forEach((listener) => listener());
}

export function subscribeAiResilience(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function normalizePath(url?: string | null): string {
  if (!url) return '';
  try {
    const parsed = url.startsWith('http') ? new URL(url) : null;
    const path = parsed ? parsed.pathname : url;
    return path.startsWith('/') ? path : `/${path}`;
  } catch {
    return url.startsWith('/') ? url : `/${url}`;
  }
}

export function resolveAiContourFamily(url?: string | null): AiContourFamily | null {
  const path = normalizePath(url);
  if (!path) return null;
  if (path.includes('/engagement-ai/') || path.endsWith('/engagement-ai')) {
    return 'engagement-ai';
  }
  if (path.includes('/ai-assistant/') || path.endsWith('/ai-assistant')) {
    return 'ai-assistant';
  }
  return null;
}

/** Стабильная ссылка на состояние контура — для useSyncExternalStore getSnapshot. */
export function getAiContourStateSnapshot(family: AiContourFamily): AiContourState {
  return families[family];
}

export function getAiContourAvailability(family: AiContourFamily): AiContourAvailability {
  const state = families[family];
  const degradedActive = state.status === 'degraded' && Date.now() < state.degradedUntil;
  const unavailable = state.status === 'unavailable';
  return {
    available: !unavailable && !degradedActive,
    status: state.status,
    unavailable,
    degraded: degradedActive,
    reason: unavailable ? UNAVAILABLE_REASON : degradedActive ? DEGRADED_REASON : null,
  };
}

/** Нужно ли погасить запрос на уровне интерсептора (не выходя в сеть). */
export function isAiRequestBlocked(url?: string | null): boolean {
  const family = resolveAiContourFamily(url);
  if (!family) return false;
  return !getAiContourAvailability(family).available;
}

export function isAiShortCircuitError(error: unknown): error is AiShortCircuitError {
  return Boolean(
    error &&
      typeof error === 'object' &&
      (error as { __aiCircuitShortCircuit?: boolean }).__aiCircuitShortCircuit === true
  );
}

export function createAiShortCircuitError(
  family: AiContourFamily,
  config?: unknown
): AiShortCircuitError {
  const error = new Error(`AI contour "${family}" temporarily unavailable`) as AiShortCircuitError;
  error.name = 'AiCircuitOpenError';
  error.__aiCircuitShortCircuit = true;
  error.code = 'AI_CIRCUIT_OPEN';
  error.family = family;
  error.config = config;
  return error;
}

function classifyFailure(error: AxiosError): 'unavailable' | 'degraded' | null {
  const status = error.response?.status;
  // 404/405/501 — эндпоинта нет на этом сервере: не ретраить, гасить на сессию.
  if (status === 404 || status === 405 || status === 501) {
    return 'unavailable';
  }
  // 401/403 подтверждают, что контур отвечает. Обновление сессионного токена
  // выполняется ниже в HTTP-клиенте, поэтому circuit здесь открывать нельзя.
  if (status === 401 || status === 403) {
    return null;
  }
  // Нет ответа (сеть/таймаут) либо 5xx — временная деградация с бэкоффом.
  return 'degraded';
}

export function noteAiFailure(url: string | null | undefined, error: AxiosError): void {
  const family = resolveAiContourFamily(url);
  if (!family) return;
  // Собственные короткозамкнутые ошибки не считаем повторными сбоями.
  if (isAiShortCircuitError(error)) return;

  const current = families[family];
  const kind = classifyFailure(error);
  if (kind === null) return;

  if (kind === 'unavailable') {
    if (current.status !== 'unavailable') {
      families[family] = {
        status: 'unavailable',
        degradedUntil: 0,
        failureStreak: current.failureStreak + 1,
        degradedToastShown: current.degradedToastShown,
      };
      emit();
    }
    return;
  }

  const failureStreak = current.failureStreak + 1;
  const backoff = Math.min(DEGRADED_BASE_MS * 2 ** (failureStreak - 1), DEGRADED_MAX_MS);
  const shouldToast = !current.degradedToastShown;
  families[family] = {
    status: 'degraded',
    degradedUntil: Date.now() + backoff,
    failureStreak,
    degradedToastShown: true,
  };
  if (shouldToast) {
    // Максимум ОДИН тост за сессию на контур — не на каждый тик.
    messageService.warning('AI-подсказки временно недоступны — продолжаю в обычном режиме');
  }
  emit();
}

export function noteAiSuccess(url: string | null | undefined): void {
  const family = resolveAiContourFamily(url);
  if (!family) return;
  const current = families[family];
  if (
    current.status !== 'ok' ||
    current.failureStreak !== 0 ||
    current.degradedToastShown
  ) {
    families[family] = createInitialState();
    emit();
  }
}

/** Тестовый хук: сброс состояния (не для прод-путей). */
export function __resetAiResilienceForTests(): void {
  families['engagement-ai'] = createInitialState();
  families['ai-assistant'] = createInitialState();
  emit();
}
